import React from "react";
import profilePhotos from "./profilePhotos.json";
import GeocodeCard from "./components/GeocodeCard";
import UpdateLayerCard from "./components/UpdateLayerCard";
import SprayNotificationsCard from "./components/SprayNotificationsCard";
import SprayNotifyCard from "./components/SprayNotifyCard";
import DiseaseMapGenerationCard from "./components/DiseaseMapGenerationCard";
import BarcodeScannerCard from "./components/BarcodeScannerCard";
import LayerClipperCard from "./components/LayerClipperCard";
import WarrantReturnsCard from "./components/WarrantReturnsCard";
import CarcassReportCard from "./components/CarcassReportCard";
import TimesheetGeneratorCard from "./components/TimesheetGeneratorCard";
import LeaveBalanceTrackerCard from "./components/LeaveBalanceTrackerCard";
import SiteReviewCard from "./components/SiteReviewCard";
import SprayRoutesCard from "./components/SprayRoutesCard";
import MailerLabelsCard from "./components/MailerLabelsCard";
import MarkTreatedPanel from "./components/run/MarkTreatedPanel";
import ClearTreatmentPanel from "./components/run/ClearTreatmentPanel";
import VectorSurvSyncCard from "./components/VectorSurvSyncCard";
import VerificationWatcherCard from "./components/VerificationWatcherCard";
import AccessWeeklyUpdateCard from "./components/AccessWeeklyUpdateCard";
import AbundanceReportCard from "./components/AbundanceReportCard";

export type Accent = "sle" | "wnv" | "brand" | "info";

export interface ToolDef {
  id: string;
  name: string;
  description: string;
  icon: string;
  live: boolean;
  render?: () => React.ReactNode;

  /**
   * Free-form tags. These drive grouping and the assistant's search, and are
   * deliberately separate from the topic categories below: a tool belongs to
   * exactly one topic but can carry any number of tags.
   *
   * Use plain words people would actually say ("pools", "warrant", "csv"),
   * not internal jargon -- the assistant matches user questions against these.
   */
  tags?: string[];

  /** Plain-language phrases the assistant should match on, beyond the name. */
  aliases?: string[];
}

export interface ToolCategory {
  name: string;
  accent: Accent;
  tools: ToolDef[];
}

/**
 * A person-curated set of tools -- "Amy's Data", "Jodi's Hacks".
 *
 * These are shared shortcuts, not permissions: everyone sees every collection.
 * The point is that people find tools faster via "the things Amy uses" than via
 * an abstract topic taxonomy.
 */
export interface Collection {
  id: string;
  /**
   * AGOL username. The authoritative link to the person: full name and profile
   * photo are read from AGOL at runtime, so this file never drifts from the
   * org directory when someone changes their name or picture.
   */
  agolUsername: string;
  /** Fallback name, shown before the AGOL profile loads (or if it fails). */
  person: string;
  /** Display label, e.g. "Amy's Data". */
  label: string;
  /** Shown in the avatar circle when no photo is set. */
  initials: string;
  /**
   * Optional profile picture, e.g. "/avatars/amy.jpg" served from
   * frontend/public/avatars/. When absent the initials circle is used, so
   * photos can be added one person at a time without touching any component.
   */
  photo?: string;
  accent: Accent;
  toolIds: string[];
}

export const accentClasses: Record<Accent, { dot: string; tint: string; text: string }> = {
  sle: { dot: "bg-sle", tint: "bg-sle-tint", text: "text-sle" },
  wnv: { dot: "bg-wnv", tint: "bg-wnv-tint", text: "text-wnv" },
  brand: { dot: "bg-brand", tint: "bg-brand-tint", text: "text-brand-deep" },
  info: { dot: "bg-info", tint: "bg-info-tint", text: "text-info" },
};

export const toolCategories: ToolCategory[] = [
  {
    name: "Surveillance & Data",
    accent: "sle",
    tools: [
      { id: "disease-map-generation", name: "Disease Positive Analysis", description: "Analyze weekly disease positives, export shapefiles, feed spray notifications.", icon: "🦟", live: true, render: () => <DiseaseMapGenerationCard /> },
      { id: "update-surveillance", name: "Update Surveillance Dashboard", description: "Upload VectorSurv collections CSV to the internal surveillance dashboard.", icon: "📡", live: true, render: () => <UpdateLayerCard layerType="surveillance" /> },
      { id: "update-disease", name: "Update Disease Dashboard", description: "Upload VectorSurv pools CSV to the public disease monitoring dashboard.", icon: "🧪", live: true, render: () => <UpdateLayerCard layerType="disease" /> },
      { id: "geocode", name: "Geocode Addresses", description: "Add standardised addresses and X/Y coordinates to a CSV, in one pass.", icon: "📍", live: true, render: () => <GeocodeCard /> },
    ],
  },
  {
    name: "Adult Control",
    accent: "wnv",
    tools: [
      { id: "treatment-builder", name: "Treatment Area Builder", description: "Pick subgrids, shape treatment boundaries, and export Community Notice maps in-app.", icon: "🗺️", live: true, render: () => <a href="/builder" className="btn-primary inline-block">Open Treatment Builder (full screen)</a> },
      { id: "spray-notifications", name: "Spray Notifications (CSV)", description: "Generate the resident notification list as a CSV from positive subgrids.", icon: "📋", live: true, render: () => <SprayNotificationsCard /> },
      { id: "spray-notify-email", name: "Spray Notifications (Email & Text)", description: "Notify the residents inside this week's treatment areas, each the way they asked to be contacted. Test first, then send.", icon: "✉️", live: true, render: () => <SprayNotifyCard /> },
      { id: "clear-treatment", name: "Clear Treatment Schedule", description: "End-of-week: flip all Upcoming treatment areas to Completed so the website stops showing them.", icon: "🗂️", live: true, render: () => <ClearTreatmentPanel /> },
      { id: "mark-treated", name: "Mark Treated (AGOL)", description: "Flip treated subgrids' Status field on the surveillance dashboard.", icon: "✅", live: true, render: () => <MarkTreatedPanel /> },
    ],
  },
  {
    name: "Field & Records",
    accent: "brand",
    tools: [
      { id: "barcode-scanner", name: "Barcode Scanner for CE", description: "Scan barcodes for continuing education records. Not working yet -- kept visible as work in progress.", icon: "📷", live: false, render: () => <BarcodeScannerCard /> },
      { id: "carcass-report", name: "Carcass Report", description: "Pull dead bird reports straight from VectorSurv, or upload the export, and see which fall inside the District, which outside, and which need a look. Also shows the watcher that emails new ones.", icon: "🐦", live: true, tags: ["dead bird", "carcass", "boundary", "geocode"], aliases: ["dead bird report", "is this address in the district"], render: () => <CarcassReportCard /> },
      { id: "timesheet-generator", name: "Timesheet Generator", description: "Generate each pay period's timecard tab instead of rebuilding it by hand. Not built yet.", icon: "🗓️", live: false, tags: ["timesheet", "timecard", "payroll", "pay period"], aliases: ["make the next timecard", "generate timesheets"], render: () => <TimesheetGeneratorCard /> },
      { id: "leave-balance-tracker", name: "Leave Balance Tracker", description: "Vacation, sick and CTO accrual, usage and balance, from the timecards. Not built yet.", icon: "🏖️", live: false, tags: ["leave", "vacation", "sick", "cto", "balance", "accrual"], aliases: ["how much vacation is left", "leave balances"], render: () => <LeaveBalanceTrackerCard /> },
      { id: "site-review", name: "Site Review", description: "Consecutive-dry and no-treatment site analysis with reports.", icon: "🔎", live: true, render: () => <SiteReviewCard /> },
      { id: "spray-routes", name: "Spray Routes", description: "Which subgrids hold surveillance sites, and which of them still need a spray route drawn.", icon: "🛣️", live: true, tags: ["spray", "routes", "subgrids", "adulticide", "surveillance"], aliases: ["what routes still need drawing", "subgrids without a spray route", "where do we still need routes"], render: () => <SprayRoutesCard /> },
      { id: "mailer-labels", name: "Mailer Labels", description: "Generate Avery 5160 mailing labels by zone from the mailer list.", icon: "🏷️", live: true, render: () => <MailerLabelsCard /> },
      { id: "access-weekly-update", name: "Recheck Report Data", description: "Pull inspections, treatments or the full sites list from FieldSeeker and copy the prepared rows into the Access database, so the recheck reports run on current data.", icon: "🗃️", live: true, render: () => <AccessWeeklyUpdateCard /> },
      { id: "abundance-report-data", name: "Abundance Report Data", description: "Prepare the VectorSurv abundance and surveillance sites exports for the Access paste, and flag collections whose site is missing.", icon: "🦟", live: true, render: () => <AbundanceReportCard /> },
      { id: "warrant-returns", name: "Warrant Returns", description: "Warrant return documents from inspection records. Reads parcels and addresses from AGOL, or from shapefiles you upload.", icon: "📄", live: true, render: () => <WarrantReturnsCard /> },
    ],
  },
  {
    name: "GIS Utilities",
    accent: "info",
    tools: [
      { id: "layer-clipper", name: "Layer Clipper", description: "Clip AGOL layers to the district boundary.", icon: "✂️", live: true, render: () => <LayerClipperCard /> },
    ],
  },
  {
    name: "Automated Jobs",
    accent: "info",
    tools: [
      { id: "vectorsurv-sync", name: "VectorSurv Sync Bot", description: "Keeps the surveillance and disease dashboard layers in step with VectorSurv. Check that it is running.", icon: "🔄", live: true, tags: ["sync", "vectorsurv", "dashboard", "bot", "schedule"], aliases: ["is the sync running", "did the dashboards update", "when did vectorsurv last sync"], render: () => <VectorSurvSyncCard /> },
      { id: "verification-watcher", name: "Verification Alerts Bot", description: "Emails the field supervisor when a verification inspection record is entered. Check that it's running.", icon: "📬", live: true, render: () => <VerificationWatcherCard /> },
    ],
  },
];

/**
 * Person-curated collections. Curated in code by Salman, from his mapping on
 * 2026-08-18 -- there is no writable store yet, so changing these means a commit.
 *
 * A tool can appear in several collections; that is the point. Anything nobody
 * claims falls into "Everything else" on the Tools page rather than vanishing.
 */
const curatedCollections: Collection[] = [
  {
    id: "amy",
    agolUsername: "amyg_CMAD",
    person: "Amy Garcia",
    label: "Amy's Data",
    initials: "AG",
    accent: "brand",
    toolIds: ["access-weekly-update", "abundance-report-data", "verification-watcher", "site-review", "warrant-returns", "layer-clipper"],
  },
  {
    id: "salman",
    agolUsername: "ssakib",
    person: "Salman Sakib",
    label: "Salman's Stack",
    initials: "SS",
    accent: "info",
    toolIds: [
      "spray-routes",
      "vectorsurv-sync",
      "disease-map-generation",
      "update-surveillance",
      "update-disease",
      "geocode",
      "treatment-builder",
      "spray-notifications",
      "clear-treatment",
      "mark-treated",
      "warrant-returns",
    ],
  },
  {
    id: "derek",
    agolUsername: "dhill54",
    person: "Derek Hill",
    label: "Derek's Crew",
    initials: "DH",
    accent: "wnv",
    toolIds: ["verification-watcher", "site-review"],
  },
  {
    id: "jodi",
    agolUsername: "jodiholeman",
    person: "Jodi Holeman",
    // Not a set of tools she runs: she reviews and tests them before anyone
    // else relies on them, which is a different relationship to the shelf.
    label: "Jodi's Bridge",
    initials: "JH",
    accent: "sle",
    toolIds: ["site-review", "vectorsurv-sync", "treatment-builder", "mark-treated", "warrant-returns"],
  },
  {
    id: "karan",
    agolUsername: "kcox_CMAD",
    person: "Karan Cox",
    label: "Karan's Office",
    initials: "KC",
    accent: "wnv",
    toolIds: ["timesheet-generator", "leave-balance-tracker", "site-review",
              "mailer-labels"],
  },
  {
    id: "annie",
    agolUsername: "amunoz_CMAD",
    person: "Annie Munoz",
    label: "Annie's Desk",
    initials: "AM",
    accent: "sle",
    toolIds: ["mailer-labels"],
  },
  {
    // The photo was parked under this key before the toolset existed, so it
    // resolves without touching profilePhotos.json.
    id: "chris",
    agolUsername: "cmonis94",
    person: "Chris Monis",
    // Rural Program Coordinator, supervising three area supervisors. The
    // shelf is named for the coordinating rather than the ground: his tools
    // all answer where crews go and what they found there.
    label: "Chris's Dispatch",
    initials: "CM",
    accent: "info",
    toolIds: ["spray-routes", "site-review", "verification-watcher"],
  },
  {
    id: "jovana",
    // Benavides, confirmed by Salman on 2026-10-07. Her email signature and
    // address spell it Benavidez with a z, and VectorSurv spells it with an
    // s -- the s is correct, so the signature is the thing that is wrong.
    // The ArcGIS username is jbenavides49, NOT a guess from the spelling: it
    // was first entered as jbenavides, which locked her out at sign-in.
    agolUsername: "jbenavides49",
    person: "Jovana Benavides",
    label: "Jovana's Bench",
    initials: "JB",
    accent: "wnv",
    toolIds: ["carcass-report", "vectorsurv-sync"],
  },
  {
    id: "kathy",
    agolUsername: "kramirez_CMAD",
    person: "Kathy Ramirez",
    label: "Kathy's Lab",
    initials: "KR",
    accent: "brand",
    toolIds: ["spray-notifications", "spray-notify-email", "vectorsurv-sync"],
  },
];

/**
 * Curated photos, resized at the gateway.
 *
 * These win over whatever AGOL holds. The AGOL picture is whatever the person
 * last uploaded to the org directory -- often nothing, and never a photo chosen
 * for this page -- so the JSON is the deliberate source and AGOL is the
 * fallback, not the other way round. Anyone absent from the JSON keeps their
 * AGOL photo and then their initials, so it can be filled in one person at a
 * time.
 *
 * Keys are collection ids. A key with no matching collection is simply unused,
 * which is how a photo can be parked before the toolset it belongs to exists.
 */
const photoOverrides = profilePhotos as Record<string, string>;

/** Longest edge we ever draw an avatar at (56px), doubled for retina. */
const AVATAR_PX = 128;

/**
 * Ask the gateway for an avatar-sized image.
 *
 * The uploads are full-size -- most are around 600KB -- and they are drawn at
 * 36-56px, so sending the originals would cost several megabytes to paint a row
 * of thumbnails. Pinata's dedicated gateways resize at the edge, which takes
 * each one to roughly 2KB and needs no re-uploading; the CID stays the source
 * of truth.
 *
 * Guarded on the host because these are Pinata-specific parameters. Anything
 * served elsewhere is passed through untouched rather than sent a query string
 * its host may not understand.
 */
function sizedPhoto(url: string): string {
  if (!/^https:\/\/[^/]+\.mypinata\.cloud\//.test(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return (
    `${url}${sep}img-width=${AVATAR_PX}&img-height=${AVATAR_PX}` +
    `&img-fit=cover&img-format=webp&img-quality=80`
  );
}

export const collections: Collection[] = curatedCollections.map((c) => ({
  ...c,
  photo: photoOverrides[c.id] ? sizedPhoto(photoOverrides[c.id]) : c.photo,
}));

/** A curated, avatar-sized photo for this collection, if one is set. */
export function curatedPhoto(id: string): string | undefined {
  const raw = photoOverrides[id];
  return raw ? sizedPhoto(raw) : undefined;
}

/** Every tool, flattened out of its topic category. */
export const allTools: ToolDef[] = toolCategories.flatMap((c) => c.tools);

export function findTool(id: string): { tool: ToolDef; category: ToolCategory } | null {
  for (const category of toolCategories) {
    const tool = category.tools.find((t) => t.id === id);
    if (tool) return { tool, category };
  }
  return null;
}

/** Resolve a collection's tool IDs to real tools, dropping any that no longer exist. */
export function toolsInCollection(collection: Collection): ToolDef[] {
  return collection.toolIds
    .map((id) => allTools.find((t) => t.id === id))
    .filter((t): t is ToolDef => Boolean(t));
}

/** Collections a given person appears in, matched loosely on full name. */
export function collectionsFor(fullName: string | null | undefined): Collection[] {
  if (!fullName) return [];
  const needle = fullName.trim().toLowerCase();
  return collections.filter(
    (c) =>
      c.person.toLowerCase() === needle ||
      // Match on first name too: AGOL full names aren't always exact.
      c.person.toLowerCase().split(" ")[0] === needle.split(" ")[0]
  );
}
